import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import {
  type ExecutionLogWithHistory,
  zodExecutionLogWithHistorySchema,
} from './validations';

/**
 * Fetches a single execution log entry (latest version) together with its full
 * version history. The backend only resolves history when the given id is the
 * latest version of the run — which is exactly what the log list returns.
 *
 * @param logId - The id of the execution log entry to fetch.
 * @param options - Optional request options (e.g., abort signal).
 * @returns The execution log entry with its version history.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Subscription
 */
export const fetchExecutionLogDetail = async (
  logId: UUID,
  options?: KyRequestOptions,
): Promise<ExecutionLogWithHistory> => {
  const response = await vestigasApi
    .get(ENDPOINT.DATA_SUBSCRIPTION.GET_EXECUTION_LOG_DETAIL(logId), options)
    .json<ExecutionLogWithHistory>();

  const validated = zodExecutionLogWithHistorySchema.safeParse(response);

  if (!validated.success) {
    console.warn(
      '[fetchExecutionLogDetail] Response validation failed',
      validated.error,
    );
    return response;
  }

  return validated.data;
};
